import { memo } from 'react'

import { GameId, StoreOfferProduct } from '@common'
import cx from 'classnames'
import { useTranslations } from 'next-intl'

import { LinkButton } from '@/components/buttons/Button'
import { useIsLoggedIn } from '@/features/common/useIsLoggedIn'
import { OfferCard } from '@/features/storefronts/_storeBase/card/v2/OfferCard'
import { isNewVisualsEnabled } from '@/features/storefronts/_storeBase/card/v2/shared'
import { STORE_BG } from '@/features/storefronts/_storeBase/constants'
import { useGameSlug } from '@/features/storefronts/game-provider'
import { useBreakpoints } from '@/utils/useBreakpoints'
import { useIsGameAccountConnected } from '@/utils/useIsGameAccountConnected'

import { getShortSlugNotHD } from '../../../../fixtures/games'
import { LoginButton } from '../../../header/LoginButton'
import { CardBundle } from '../card/CardBundle'
import { StoreTypography } from '../typography/StoreTypography'

import styles from './bundles.module.scss'
import storeStyles from '../storefronts.module.scss'

const Descriptions = ({
  isLoggedIn,
  isDesktop,
  isConnected,
  hasBundles,
  shortSlug,
}: {
  isLoggedIn: boolean
  isDesktop: boolean
  isConnected: boolean
  hasBundles: boolean
  shortSlug: ReturnType<typeof getShortSlugNotHD>
}) => {
  const t = useTranslations()
  const typography = {
    tag: 'p',
    style: 'plain',
    textSize: isDesktop ? 'md' : 'body',
    overrideFont: 'supercellText',
    lineHeight: 'normal',
  } as const

  if (!isLoggedIn) {
    return <StoreTypography {...typography}>{t('specials_login')}</StoreTypography>
  }

  if (isConnected) {
    return (
      <StoreTypography {...typography}>
        {hasBundles ? t.rich(`${shortSlug}_specials_available`) : t('specials_not_available')}
      </StoreTypography>
    )
  }

  return (
    <>
      <StoreTypography {...typography}>{t(`${shortSlug}_specials_connect_account_title`)}</StoreTypography>
      <StoreTypography {...typography} textSize="sm">
        {t('store_specials_connect_account_subtitle')}
      </StoreTypography>
    </>
  )
}

export const Bundles = memo(
  ({ bundles, enableNewVisuals }: { bundles: StoreOfferProduct[]; enableNewVisuals?: GameId[] }) => {
    const t = useTranslations()
    const from = useBreakpoints().from
    const gameSlug = useGameSlug()
    const shortSlug = getShortSlugNotHD(gameSlug)
    const isDesktop = from('tablet')
    const loggedIn = useIsLoggedIn()
    const isConnected = useIsGameAccountConnected(gameSlug)
    const newSpecialOfferCardsEnabled = isNewVisualsEnabled(gameSlug, enableNewVisuals)
    const shouldUseClashiversaryTitle = isClashiversary(gameSlug)
    const title = shouldUseClashiversaryTitle ? t('clashiversary_specials') : t('specials_title')

    return (
      <div
        className={cx(
          styles.bundles,
          styles[gameSlug],
          storeStyles[gameSlug],
          storeStyles.topOverlap,
          storeStyles.bottomOverlap,
        )}
        style={{ ...(gameSlug !== 'brawlstars' && { backgroundImage: STORE_BG(gameSlug) }) }}
      >
        {gameSlug === 'brawlstars' && (
          <div className={styles.rotatedBg} style={{ backgroundImage: STORE_BG(gameSlug) }} />
        )}
        <div id="storespecials" className={styles.specialsHeading}>
          <div className={styles.title}>
            <StoreTypography className={styles.titleText} tag="h1" textSize={isDesktop ? 'xl' : 'lg'} style="outlined">
              {title}
            </StoreTypography>
          </div>
          <Descriptions
            isLoggedIn={loggedIn}
            isDesktop={isDesktop}
            isConnected={isConnected}
            hasBundles={!!bundles.length}
            shortSlug={shortSlug}
          />
          {!loggedIn ? <LoginButton /> : null}
          {loggedIn && !isConnected ? (
            <LinkButton href={t(`${shortSlug}_open_game_link`)} additionalClasses={styles.gameButton}>
              {t(`open_${shortSlug}`)}
            </LinkButton>
          ) : null}
        </div>
        {isConnected ? (
          <div className={styles.bundlesContainer}>
            {bundles.map((product, index) =>
              newSpecialOfferCardsEnabled ? (
                <OfferCard
                  key={product.id}
                  product={product}
                  offerCardType={'special'}
                  enableNewVisuals={enableNewVisuals}
                />
              ) : (
                <CardBundle key={index} game={gameSlug} bundle={product} />
              ),
            )}
          </div>
        ) : null}
      </div>
    )
  },
)

Bundles.displayName = 'Bundles'

function isClashiversary(gameId: GameId) {
  if (gameId !== 'clashofclans') {
    return false
  }

  const [date, time] = new Date().toISOString().split('T')
  const [, month, day] = date.split('-')
  const [hour] = time.split(':')

  const parsedMonth = parseInt(month, 10)
  const parsedDay = parseInt(day, 10)
  const parsedHour = parseInt(hour, 10)

  if (parsedMonth === 8) {
    if (parsedDay === 2) {
      return parsedHour >= 8
    }
    if (parsedDay === 17) {
      return parsedHour < 8
    }
    return parsedDay > 2 && parsedDay < 17
  }

  return false
}
